// ──────────────────────────────────────────────────────────────────────────
// IL LOGO DEL CIRCOLO
//
// In cima al telefono il circolo è un tondo: il suo logo, o senza logo la
// sua iniziale. Il nome intero si legge toccandolo.
//
// Il logo sta sul documento del circolo, come immagine già rimpicciolita
// (un data URL): è piccolo, arriva insieme al nome e funziona anche senza
// rete, senza un secondo posto dove tenere i file.
//
// La foto del maestro è fatta allo stesso modo e passa dagli stessi
// attrezzi (si apre, si ritaglia nel cerchio, si rimpicciolisce); sta sul
// documento del maestro, e i colleghi la ricevono col suo nome.
// ──────────────────────────────────────────────────────────────────────────

// il lato del quadrato in cui finisce il logo: il tondo più grande che lo
// mostra è di 64px, e così resta nitido anche sugli schermi densi
export const LOGO_SIZE = 192

// quanto può pesare il data URL: lo stesso tetto lo controlla `updateSchool`
export const LOGO_MAX_LENGTH = 200 * 1024

// Le parole che dicono cos'è un circolo e non quale: "Tennis Club Bologna"
// e "Tennis Club Milano" farebbero tutti e due "T", e passando dall'uno
// all'altro il tondo non cambierebbe.
const GENERIC_WORDS = new Set([
	"asd",
	"ssd",
	"ad",
	"as",
	"ac",
	"sc",
	"us",
	"tc",
	"ct",
	"circolo",
	"club",
	"country",
	"sporting",
	"sport",
	"sportivo",
	"tennis",
	"padel",
	"associazione",
	"societa",
	"centro",
	"polisportiva",
	"il",
	"la",
	"lo",
	"gli",
	"le",
	"del",
	"della",
	"dello",
	"dei",
	"degli",
	"delle",
	"di",
	"da",
	"al",
	"alla",
	"the",
])

const words = name =>
	(name ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.split(/[^\p{L}\p{N}]+/u)
		.filter(Boolean)

// una lettera sola è un pezzo di sigla: "A.S.D." non dice quale circolo
const isTelling = word => word.length > 1 && !GENERIC_WORDS.has(word.toLowerCase())

// La lettera del circolo: la prima della prima parola che lo distingue, e
// se il nome è fatto solo di parole generiche la prima e basta.
export const schoolInitial = name => {
	const all = words(name)
	if (all.length === 0) return "?"
	const telling = all.find(isTelling) ?? all[0]
	return telling[0].toUpperCase()
}

// L'immagine scelta, aperta: resta aperta finché si ritaglia, e chi la
// apre la chiude con `close` quando ha finito.
export const openImage = file =>
	new Promise((resolve, reject) => {
		if (!file?.type?.startsWith("image/")) {
			reject(new Error("Scegli un'immagine"))
			return
		}
		const url = URL.createObjectURL(file)
		const close = () => URL.revokeObjectURL(url)
		const image = new Image()
		image.onload = () => {
			const width = image.naturalWidth || image.width
			const height = image.naturalHeight || image.height
			if (!width || !height) {
				close()
				reject(new Error("Questa immagine non si riesce ad aprire"))
				return
			}
			resolve({ image, url, width, height, close })
		}
		image.onerror = () => {
			close()
			reject(new Error("Questa immagine non si riesce ad aprire"))
		}
		image.src = url
	})

// ──────────────────────────────────────────────────────────────────────────
// IL RITAGLIO
//
// Si guarda l'immagine dentro un cerchio largo `view` pixel, lo stesso
// cerchio in cui il logo starà in cima allo schermo. Il ritaglio è
// `{ scale, x, y }`: quanti pixel del cerchio fa un pixel dell'immagine, e
// dove sta il centro dell'immagine rispetto al centro del cerchio.
//
// Si parte dall'immagine intera, un po' dentro il bordo: un logo largo non
// perde i lati finché non si decide di ingrandirlo. Rimpicciolendo resta
// del bianco attorno; ingrandendo si arriva fino a quattro volte il
// cerchio pieno, abbastanza per prendere un dettaglio di una foto.
// ──────────────────────────────────────────────────────────────────────────

// quanto del cerchio prende l'immagine intera, all'inizio
const INITIAL_FILL = 0.82

export const cropLimits = (width, height, view) => {
	const contain = view / Math.max(width, height)
	const cover = view / Math.min(width, height)
	return { min: contain * 0.5, max: cover * 4, initial: contain * INITIAL_FILL }
}

// Una foto invece parte che riempie il cerchio: una faccia con del bianco
// attorno non la vuole nessuno, e ritagliarla è quasi sempre stringere.
export const initialCrop = (width, height, view, { cover = false } = {}) => ({
	scale: cover ? view / Math.min(width, height) : cropLimits(width, height, view).initial,
	x: 0,
	y: 0,
})

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

// Il centro dell'immagine non esce dal cerchio: oltre, nel cerchio
// resterebbe solo un angolo, o niente.
export const clampCrop = ({ scale, x, y }, width, height, view) => {
	const { min, max } = cropLimits(width, height, view)
	const half = view / 2
	return { scale: clamp(scale, min, max), x: clamp(x, -half, half), y: clamp(y, -half, half) }
}

// Ingrandire tiene fermo quello che sta al centro del cerchio: con le dita,
// con la rotellina o con la barra, si ingrandisce quello che si guarda.
export const zoomCrop = (crop, nextScale, width, height, view) => {
	const { min, max } = cropLimits(width, height, view)
	const scale = clamp(nextScale, min, max)
	const ratio = scale / crop.scale
	return clampCrop({ scale, x: crop.x * ratio, y: crop.y * ratio }, width, height, view)
}

// Dove finisce l'immagine nel quadrato del logo, lato `side`: lo stesso
// posto in cui la si vedeva nel cerchio, in scala.
export const cropRect = ({ scale, x, y }, width, height, view, side) => {
	const k = side / view
	const drawnWidth = width * scale * k
	const drawnHeight = height * scale * k
	return {
		left: side / 2 + x * k - drawnWidth / 2,
		top: side / 2 + y * k - drawnHeight / 2,
		width: drawnWidth,
		height: drawnHeight,
	}
}

// Il logo ritagliato, nel quadrato da `LOGO_SIZE`: il cerchio lo taglia
// chi lo mostra, e quello che è fuori dall'immagine resta trasparente
// (il tondo ha il fondo bianco).
//
// Si prova il WebP, che pesa meno; Safari vecchi non lo sanno scrivere e
// restituiscono un PNG. Se nemmeno così ci sta, un JPEG su fondo bianco.
export const renderLogo = ({ image, width, height }, crop, view, what = "Il logo") => {
	const side = LOGO_SIZE
	const rect = cropRect(crop, width, height, view, side)

	const draw = background => {
		const canvas = document.createElement("canvas")
		canvas.width = side
		canvas.height = side
		const context = canvas.getContext("2d")
		if (background) {
			context.fillStyle = background
			context.fillRect(0, 0, side, side)
		}
		context.imageSmoothingQuality = "high"
		context.drawImage(image, rect.left, rect.top, rect.width, rect.height)
		return canvas
	}

	const transparent = draw(null)
	const webp = transparent.toDataURL("image/webp", 0.9)
	if (webp.startsWith("data:image/webp") && webp.length <= LOGO_MAX_LENGTH) return webp

	const png = transparent.toDataURL("image/png")
	if (png.length <= LOGO_MAX_LENGTH) return png

	const jpeg = draw("#ffffff").toDataURL("image/jpeg", 0.85)
	if (jpeg.length <= LOGO_MAX_LENGTH) return jpeg

	throw new Error(`${what} è troppo pesante: prova con un'immagine più semplice`)
}
